import React, { useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Radio,
  Spin,
  message,
  Divider,
} from "antd";
import {
  useRepairJobCreateMutation,
  useRepairJobUpdateMutation,
  useCustomersQuery,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useActivePaymentStatusesQuery,
  useActiveOrderStatusesQuery,
  useCreateOrderMutation,
} from "../../../Services";
import { RepairJob } from "../../../Types/RepairJobTypes";
import { drawerTitle, drawerFooter } from "../../../components/AppUi";
import dayjs from "dayjs";
import { PaymentOption, PaymentOptionValue } from "../../../components";
import { TemporaryItemProduct } from "../../../Enums/TemporaryItem";
import { PaymentStatus, getPaymentStatusCode, OrderStatus, getOrderStatusCode } from "../../../Enums";

interface RepairJobFormDrawerProps {
  open: boolean;
  onClose: () => void;
  job: RepairJob | null;
  onSuccess: () => void;
  refetch: () => void;
}

const RepairJobFormDrawer: React.FC<RepairJobFormDrawerProps> = ({
  open,
  onClose,
  job,
  onSuccess,
  refetch
}) => {
  const [form] = Form.useForm();
  const [customerType, setCustomerType] = React.useState<"existing" | "walkin">(
    job?.customer_id ? "existing" : "walkin",
  );

  const [paymentMethodId, setPaymentMethodId] = React.useState<string>();
  const [paymentDetails, setPaymentDetails] = React.useState<PaymentOptionValue>({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });

  const { data: paymentMethodsRes } = useActivePaymentMethodsQuery({ enabled: open });
  const { data: bankAccountsRes } = useActiveBankAccountsQuery({ enabled: open });
  const { data: paymentStatusesRes } = useActivePaymentStatusesQuery({ enabled: open });
  const { data: orderStatusesRes } = useActiveOrderStatusesQuery({ enabled: open });
  const paymentMethods = paymentMethodsRes ?? [];
  const bankAccounts = bankAccountsRes ?? [];
  const paymentStatuses = paymentStatusesRes ?? [];
  const orderStatuses = orderStatusesRes ?? [];

  const selectedPaymentMethod = React.useMemo(
    () => paymentMethods.find((pm: any) => pm.id === paymentMethodId),
    [paymentMethods, paymentMethodId]
  );
  const paymentMethodCode = selectedPaymentMethod?.code;

  const handlePaymentMethodChange = (val: string | undefined) => {
    setPaymentMethodId(val);
    setPaymentDetails({
      amount: 0,
      note: "",
      bank_account_id: "",
      transaction_no: "",
      reference_no: "",
      cheques: [],
    });
  };

  const createMutation = useRepairJobCreateMutation();
  const updateMutation = useRepairJobUpdateMutation();
  const createOrderMutation = useCreateOrderMutation();
  const isEditing = !!job;
  const { data: customersData } = useCustomersQuery({ enabled: open });

  const customers = customersData?.data ?? [];
  const isPending = createMutation.isPending || updateMutation.isPending || createOrderMutation.isPending;

  useEffect(() => {
    if (open) {
      if (job) {
        form.setFieldsValue({
          customer_id: job.customer_id,
          temp_customer_name: job.temp_customer_name,
          temp_customer_phone: job.temp_customer_phone,
          temp_customer_email: job.temp_customer_email,
          temp_customer_address: job.temp_customer_address,
          device_type: job.device_type,
          device_brand: job.device_brand,
          device_model: job.device_model,
          device_imei: job.device_imei,
          device_serial_number: job.device_serial_number,
          device_password: job.device_password,
          reported_issues: job.reported_issues,
          technician_notes: job.technician_notes,
          diagnosis: job.diagnosis,
          total_cost: job.total_cost,
          selling_price: job.selling_price,
          status: job.status,
          priority: job.priority,
          received_date: job.received_date ? dayjs(job.received_date) : null,
          promised_date: job.promised_date ? dayjs(job.promised_date) : null,
        });
        setCustomerType(job.customer_id ? "existing" : "walkin");
      } else {
        form.resetFields();
        form.setFieldsValue({
          priority: "normal",
          status: "pending",
          received_date: dayjs(),
          total_cost: 0,
          selling_price: 0,
          advance_payment: 0,
        });
        setCustomerType("walkin");
        setPaymentMethodId(undefined);
        setPaymentDetails({
          amount: 0,
          note: "",
          bank_account_id: "",
          transaction_no: "",
          reference_no: "",
          cheques: [],
        });
      }
    }
  }, [open, job, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = () => {
    form.validateFields().then((values) => {
      const advAmount = paymentDetails.amount ?? 0;
      if (advAmount > 0) {
        if (!paymentMethodId) {
          message.error("Please select a payment method for the advance payment.");
          return;
        }
        if (["card", "bank_transfer", "cheque"].includes(paymentMethodCode || "") && !paymentDetails.bank_account_id) {
          message.error("Please select a bank account for the advance payment.");
          return;
        }
        if (paymentMethodCode === "cheque" && (!paymentDetails.cheque_no || !paymentDetails.bank_name || !paymentDetails.cheque_date)) {
          message.error("Please fill in all cheque details for the advance payment.");
          return;
        }
      }

      const payload: any = {
        device_type: values.device_type,
        reported_issues: values.reported_issues,
        priority: values.priority,
        received_date: values.received_date?.format("YYYY-MM-DD HH:mm:ss"),
        promised_date: values.promised_date?.format("YYYY-MM-DD HH:mm:ss"),
        device_password: values.device_password,
        technician_notes: values.technician_notes,
        diagnosis: values.diagnosis,
        total_cost: values.total_cost ?? 0,
        selling_price: values.selling_price ?? 0,
        advance_payment: advAmount,
        status: values.status,
      };

      if (customerType === "existing" && values.customer_id) {
        payload.customer_id = values.customer_id;
      } else {
        payload.temp_customer_name = values.temp_customer_name;
        payload.temp_customer_phone = values.temp_customer_phone;
        payload.temp_customer_email = values.temp_customer_email;
        payload.temp_customer_address = values.temp_customer_address;
      }

      if (advAmount > 0) {
        payload.payment_method_id = paymentMethodId;
        payload.bank_account_id = paymentDetails.bank_account_id || null;
        payload.transaction_no = paymentDetails.transaction_no || paymentDetails.reference_no || null;
        payload.cheque_no = paymentDetails.cheque_no || null;
        payload.bank_name = paymentDetails.bank_name || null;
        payload.cheque_date = paymentDetails.cheque_date || null;
      }

      const mutation = isEditing ? updateMutation : createMutation;
      mutation.mutate(
        isEditing ? { ...payload, id: job!.id } : payload,
        {
          onSuccess: (res) => {
            if (res?.success) {
              message.success(
                isEditing
                  ? "Repair job updated successfully."
                  : "Repair job created successfully.",
              );
              form.resetFields();

              // Create an advance payment order if this is a new repair with advance
              if (!isEditing && advAmount > 0 && res.data?.id) {
                const paidStatusId = paymentStatuses.find(
                  (ps: any) => ps.code === getPaymentStatusCode(PaymentStatus.PAID),
                )?.id;
                const orderedStatusId = orderStatuses.find(
                  (os: any) => os.code === getOrderStatusCode(OrderStatus.ORDERED),
                )?.id;

                if (paidStatusId && orderedStatusId) {
                  const orderPayload: any = {
                    customer_id: payload.customer_id || null,
                    selling_type: "retail",
                    subtotal: advAmount,
                    discount_amount: 0,
                    other_charges: 0,
                    tax_amount: 0,
                    grand_total: advAmount,
                    paid_amount: advAmount,
                    due_amount: 0,
                    payment_method_id: paymentMethodId,
                    payment_status_id: paidStatusId,
                    order_status_id: orderedStatusId,
                    employee_id: null,
                    items: [{
                      product_id: TemporaryItemProduct.PRODUCT,
                      stock_id: null,
                      quantity: 1,
                      price: advAmount,
                      total_price: advAmount,
                      discount_amount: 0,
                      cost_price: 0,
                      is_tempory_item: true,
                      item_name: `Advance payment for Repair Job #${res.data.job_number || ""}`,
                      description: `Repair Job ID: ${res.data.id}`,
                    }],
                    payments: [{
                      payment_method_id: paymentMethodId,
                      amount: advAmount,
                      note: `Advance payment for Repair Job #${res.data.job_number || ""} (ID: ${res.data.id})`,
                      ...(paymentDetails.bank_account_id ? { bank_account_id: paymentDetails.bank_account_id } : {}),
                      ...(paymentDetails.transaction_no || paymentDetails.reference_no ? { transaction_no: paymentDetails.transaction_no || paymentDetails.reference_no } : {}),
                      ...(paymentDetails.cheque_no ? { cheque_no: paymentDetails.cheque_no } : {}),
                      ...(paymentDetails.bank_name ? { bank_name: paymentDetails.bank_name } : {}),
                      ...(paymentDetails.cheque_date ? { cheque_date: paymentDetails.cheque_date } : {}),
                    }],
                    repair_job_id: res.data.id,
                    is_advance_payment: true,
                    note: `Advance payment for Repair Job #${res.data.job_number || ""} (ID: ${res.data.id})`,
                  };
                  createOrderMutation.mutate(orderPayload, {
                    onSuccess: (orderRes: any) => {
                      if (orderRes?.success) {
                        message.success("Advance payment order created successfully.");
                        refetch();
                      }
                    },
                    onError: () => {
                      message.error("Failed to create advance payment order. Please check orders.");
                    },
                  });
                }
              }

              onSuccess();
            } else {
              message.error(res?.message || "Operation failed.");
            }
          },
          onError: () => {
            message.error("Something went wrong.");
          },
        },
      );
    });
  };

  return (
    <Drawer
      title={drawerTitle(
        isEditing ? "Edit Repair Job" : "New Repair Job",
        isEditing ? `Job #${job?.job_number}` : "Create a service ticket",
      )}
      open={open}
      onClose={handleCancel}
      width={800}
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: handleCancel, disabled: isPending },
          { label: isEditing ? "Update Repair Job" : "Create Repair Job", type: "primary", htmlType: "submit", loading: isPending, onClick: handleFinish },
        ],
      })}
      destroyOnClose
    >
      <Spin spinning={isPending}>
        <Form
          form={form}
          layout="vertical"
          className="pt-2"
        >
          <Divider orientation="left" className="text-xs font-semibold text-gray-500">
            Customer Information
          </Divider>

          <Form.Item label={<span className="text-xs font-semibold text-gray-600">Customer Type</span>}>
            <Radio.Group
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value)}
              disabled={isEditing}
            >
              <Radio value="walkin">Walk-in Customer</Radio>
              <Radio value="existing">Existing Customer</Radio>
            </Radio.Group>
          </Form.Item>

          {customerType === "existing" ? (
            <Form.Item
              name="customer_id"
              label={<span className="text-xs font-semibold text-gray-600">Select Customer</span>}
              rules={[{ required: true, message: "Please select a customer." }]}
            >
              <Select
                showSearch
                placeholder="Search customer..."
                optionFilterProp="label"
                options={customers.map((c: any) => ({
                  value: c.id,
                  label: `${c.name}${c.phone ? ` (${c.phone})` : ""}`,
                }))}
              />
            </Form.Item>
          ) : (
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item
                name="temp_customer_name"
                label={<span className="text-xs font-semibold text-gray-600">Customer Name</span>}
                rules={[{ required: true, message: "Please enter customer name." }]}
              >
                <Input placeholder="Enter customer name" />
              </Form.Item>
              <Form.Item
                name="temp_customer_phone"
                label={<span className="text-xs font-semibold text-gray-600">Phone Number</span>}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
              <Form.Item
                name="temp_customer_email"
                label={<span className="text-xs font-semibold text-gray-600">Email (Optional)</span>}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
              <Form.Item
                name="temp_customer_address"
                label={<span className="text-xs font-semibold text-gray-600">Address (Optional)</span>}
              >
                <Input placeholder="Enter address" />
              </Form.Item>
            </div>
          )}

          <Divider orientation="left" className="text-xs font-semibold text-gray-500">
            Device Information
          </Divider>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="device_type"
              label={<span className="text-xs font-semibold text-gray-600">Device Type *</span>}
              rules={[{ required: true, message: "Please enter device type." }]}
            >
              <Select placeholder="Select device type">
                <Select.Option value="Phone">Phone</Select.Option>
                <Select.Option value="Tablet">Tablet</Select.Option>
                <Select.Option value="Laptop">Laptop</Select.Option>
                <Select.Option value="Desktop">Desktop</Select.Option>
                <Select.Option value="Printer">Printer</Select.Option>
                <Select.Option value="Other">Other</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="device_brand"
              label={<span className="text-xs font-semibold text-gray-600">Brand</span>}
            >
              <Input placeholder="e.g. Samsung" />
            </Form.Item>
            <Form.Item
              name="device_model"
              label={<span className="text-xs font-semibold text-gray-600">Model</span>}
            >
              <Input placeholder="e.g. Galaxy S24" />
            </Form.Item>
            <Form.Item
              name="device_imei"
              label={<span className="text-xs font-semibold text-gray-600">IMEI / Serial</span>}
            >
              <Input placeholder="IMEI or serial number" />
            </Form.Item>
            <Form.Item
              name="device_serial_number"
              label={<span className="text-xs font-semibold text-gray-600">Serial Number</span>}
            >
              <Input placeholder="Serial number" />
            </Form.Item>
            <Form.Item
              name="device_password"
              label={<span className="text-xs font-semibold text-gray-600">Device Password</span>}
            >
              <Input.Password placeholder="Pattern/PIN" />
            </Form.Item>
          </div>

          <Divider orientation="left" className="text-xs font-semibold text-gray-500">
            Issue Details
          </Divider>

          <Form.Item
            name="reported_issues"
            label={<span className="text-xs font-semibold text-gray-600">Reported Issues *</span>}
            rules={[{ required: true, message: "Please describe the reported issues." }]}
          >
            <Input.TextArea rows={3} placeholder="Describe the issues reported by customer" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="technician_notes"
              label={<span className="text-xs font-semibold text-gray-600">Technician Notes</span>}
            >
              <Input.TextArea rows={3} placeholder="Internal notes for technician" />
            </Form.Item>
            <Form.Item
              name="diagnosis"
              label={<span className="text-xs font-semibold text-gray-600">Diagnosis</span>}
            >
              <Input.TextArea rows={3} placeholder="Diagnosis after inspection" />
            </Form.Item>
          </div>

          <Divider orientation="left" className="text-xs font-semibold text-gray-500">
            Cost & Schedule
          </Divider>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="total_cost"
              label={<span className="text-xs font-semibold text-gray-600">Total Cost (Store Cost) (Rs.)</span>}
            >
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>
            <Form.Item
              name="selling_price"
              label={<span className="text-xs font-semibold text-gray-600">Selling Price (Rs.)</span>}
              rules={[{ required: true, message: "Please enter selling price." }]}
            >
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>
            <Form.Item
              name="priority"
              label={<span className="text-xs font-semibold text-gray-600">Priority</span>}
            >
              <Select>
                <Select.Option value="low">Low</Select.Option>
                <Select.Option value="normal">Normal</Select.Option>
                <Select.Option value="high">High</Select.Option>
                <Select.Option value="urgent">Urgent</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="received_date"
              label={<span className="text-xs font-semibold text-gray-600">Received Date</span>}
            >
              <DatePicker className="w-full" showTime />
            </Form.Item>
            <Form.Item
              name="promised_date"
              label={<span className="text-xs font-semibold text-gray-600">Promised Date</span>}
            >
              <DatePicker className="w-full" />
            </Form.Item>
            {isEditing && (
              <Form.Item
                name="status"
                label={<span className="text-xs font-semibold text-gray-600">Status</span>}
              >
                <Select disabled={job?.status === "completed"}>
                  <Select.Option value="pending">Pending</Select.Option>
                  {job?.status === "completed" && (
                    <Select.Option value="completed">Completed</Select.Option>
                  )}
                  <Select.Option value="cancelled">Cancelled</Select.Option>
                </Select>
              </Form.Item>
            )}
          </div>

          {!isEditing && (
            <>
              <Divider orientation="left" className="text-xs font-semibold text-gray-500">
                Advance Payment (Optional)
              </Divider>

              <div className="grid grid-cols-2 gap-x-4">
                <Form.Item
                  label={<span className="text-xs font-semibold text-gray-600">Payment Method</span>}
                >
                  <Select
                    placeholder="Select payment method"
                    value={paymentMethodId}
                    onChange={handlePaymentMethodChange}
                    allowClear
                  >
                    {paymentMethods.map((pm: any) => (
                      <Select.Option key={pm.id} value={pm.id}>
                        {pm.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>

              {paymentMethodCode && (
                <div className="pt-2 border-t border-gray-100">
                  <PaymentOption
                    paymentMethodCode={paymentMethodCode}
                    value={paymentDetails}
                    onChange={setPaymentDetails}
                    bankAccounts={bankAccounts}
                  />
                </div>
              )}
            </>
          )}
        </Form>
      </Spin>
    </Drawer>
  );
};

export default RepairJobFormDrawer;
